/** RSOC campaign feed URL builder — mirrors backend `build_klow_final_url`. */

const DEFAULT_TRACKING = {
  t1: "__CAMPAIGN_ID__",
  t2: "__AID__",
  t3: "__PLACEMENT__",
} as const

const DEFAULT_TRAFFIC_SOURCE = "TT"

/** Preferred query-string order for KLOW landing URLs. */
const KLOW_FEED_PARAM_ORDER = [
  "ct",
  "stid",
  "spid",
  "ts",
  "t1",
  "t2",
  "t3",
  "u",
  "u2",
  "sqs",
  "atxt",
] as const

export interface KlowFeedUrlParams {
  rsocTemplateId?: string
  adCopyText?: string
  suggestedKeywords?: string
  trafficSource?: string
  googleTemplateId?: string
  pixelId?: string
  publisherTracking1?: string
  publisherTracking2?: string
  publisherTracking3?: string
  userTracking?: string
  userTracking2?: string
}

function buildOrderedSearch(params: Record<string, string>): string {
  const parts: string[] = []
  const seen = new Set<string>()
  for (const key of KLOW_FEED_PARAM_ORDER) {
    if (key in params) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      seen.add(key)
    }
  }
  for (const [key, value] of Object.entries(params)) {
    if (!seen.has(key)) {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    }
  }
  return parts.join("&")
}

export function isValidKlowArticleSlug(slug: string | null | undefined): boolean {
  const clean = slug?.trim().replace(/^\//, "")
  if (!clean) return false
  return !["none", "null", "undefined"].includes(clean.toLowerCase())
}

export function isKlowArticleReady(article: {
  domain?: string
  url?: string | null
  slug?: string | null
  base_url?: string | null
  generation_status?: string | null
}): boolean {
  if (article.generation_status === "IN PROGRESS") return false
  const base =
    article.base_url?.trim() ||
    buildKlowArticleBaseUrl(
      article.domain ?? "topicvista.co",
      article.slug,
      article.url,
    )
  if (!base) return false
  try {
    const path = new URL(base).pathname.replace(/^\//, "").trim()
    return isValidKlowArticleSlug(path)
  } catch {
    return false
  }
}

export function buildKlowArticleBaseUrl(
  domain: string,
  slug: string | null | undefined,
  url?: string | null,
): string {
  const stored = url?.trim()
  if (stored) {
    const parsed = new URL(stored)
    parsed.search = ""
    parsed.hash = ""
    return parsed.toString()
  }
  const cleanSlug = slug?.trim().replace(/^\//, "")
  const cleanDomain = (domain || "topicvista.co").replace(/\/$/, "")
  if (!cleanSlug) return ""
  return `https://${cleanDomain}/${cleanSlug}`
}

export function buildKlowFeedUrl(
  articleUrl: string,
  params: KlowFeedUrlParams = {},
): string {
  const base = articleUrl.trim()
  if (!base) return base

  const url = new URL(base)
  const query: Record<string, string> = Object.fromEntries(url.searchParams.entries())

  if (params.rsocTemplateId) query.ct = params.rsocTemplateId
  if (params.googleTemplateId) query.stid = params.googleTemplateId
  if (params.pixelId) query.spid = params.pixelId
  query.t1 = params.publisherTracking1 || DEFAULT_TRACKING.t1
  query.t2 = params.publisherTracking2 || DEFAULT_TRACKING.t2
  query.t3 = params.publisherTracking3 || DEFAULT_TRACKING.t3
  const sqs = params.suggestedKeywords?.trim()
  if (sqs) query.sqs = sqs
  query.atxt = params.adCopyText?.trim() ?? ""
  query.ts =
    (params.trafficSource || DEFAULT_TRAFFIC_SOURCE).trim() || DEFAULT_TRAFFIC_SOURCE
  if (params.userTracking) query.u = params.userTracking
  if (params.userTracking2) query.u2 = params.userTracking2

  url.search = buildOrderedSearch(query)
  return url.toString()
}

export function firstSuggestedHeadline(value: unknown): string | null {
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0]
    if (typeof first === "string" && first.trim()) return first.trim()
  }
  return null
}

export function parseKlowFeedUrl(url: string | null | undefined): KlowFeedUrlParams & {
  baseUrl: string
} {
  if (!url?.trim()) return { baseUrl: "" }
  try {
    const parsed = new URL(url.trim())
    const baseUrl = `${parsed.origin}${parsed.pathname}`
    return {
      baseUrl,
      rsocTemplateId: parsed.searchParams.get("ct") || undefined,
      googleTemplateId: parsed.searchParams.get("stid") || undefined,
      pixelId: parsed.searchParams.get("spid") || undefined,
      suggestedKeywords: parsed.searchParams.get("sqs") || undefined,
      adCopyText: parsed.searchParams.get("atxt") || undefined,
      trafficSource: parsed.searchParams.get("ts") || undefined,
      userTracking: parsed.searchParams.get("u") || undefined,
      userTracking2: parsed.searchParams.get("u2") || undefined,
      publisherTracking1: parsed.searchParams.get("t1") || undefined,
      publisherTracking2: parsed.searchParams.get("t2") || undefined,
      publisherTracking3: parsed.searchParams.get("t3") || undefined,
    }
  } catch {
    return { baseUrl: "" }
  }
}

export function articleSlugFromKlowUrl(url: string | null | undefined): string {
  const { baseUrl } = parseKlowFeedUrl(url)
  if (!baseUrl) return ""
  try {
    return new URL(baseUrl).pathname.replace(/^\//, "").trim()
  } catch {
    return ""
  }
}

export interface KlowOfferUrlSource {
  public_slug: string
  target_url?: string | null
  klow_domain?: string | null
  klow_article_slug?: string | null
  klow_article_url?: string | null
  klow_rsoc_template_id?: string | null
  klow_google_template_id?: string | null
  klow_pixel_id?: string | null
  klow_suggested_keywords?: string | null
  klow_ad_copy_text?: string | null
  klow_traffic_source?: string | null
}

export function buildKlowOfferTargetUrl(offer: KlowOfferUrlSource): string | null {
  const base =
    offer.klow_article_url?.trim() ||
    (offer.klow_domain && offer.klow_article_slug
      ? buildKlowArticleBaseUrl(offer.klow_domain, offer.klow_article_slug)
      : "") ||
    parseKlowFeedUrl(offer.target_url).baseUrl

  if (!base) return offer.target_url?.trim() || null

  const legacy = parseKlowFeedUrl(offer.target_url)
  return buildKlowFeedUrl(base, {
    rsocTemplateId: offer.klow_rsoc_template_id || legacy.rsocTemplateId,
    googleTemplateId: offer.klow_google_template_id || legacy.googleTemplateId,
    pixelId: offer.klow_pixel_id || legacy.pixelId,
    suggestedKeywords: offer.klow_suggested_keywords || legacy.suggestedKeywords,
    adCopyText: offer.klow_ad_copy_text ?? legacy.adCopyText,
    trafficSource: offer.klow_traffic_source || legacy.trafficSource,
    userTracking: offer.public_slug,
    publisherTracking1: legacy.publisherTracking1,
    publisherTracking2: legacy.publisherTracking2,
    publisherTracking3: legacy.publisherTracking3,
    userTracking2: legacy.userTracking2,
  })
}
